import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getTreatink } from "../lib/treatink";
import { setTkDesign } from "../store/customizerSlice";
import { persistDesignerResult } from "../components/customizer/functions/persistDesignerResult";

// Product-first designer flow (Phase 2): a page shows products, and a "Customize" CTA
// calls openDesigner({sku}) — the SDK modal opens for that product; on save the result
// is persisted through the legacy contract (persistDesignerResult), stored as tkDesign,
// and handed to onSaved (which typically composites + adds to cart). The modal is
// closed automatically if the host component unmounts.

export function useSdkDesigner() {
  const dispatch = useDispatch();
  const cartItems = useSelector((s) => s.cart.cartItems);
  const cartRef = useRef(cartItems);
  cartRef.current = cartItems;
  const tkRef = useRef(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    // Warm the SDK module on mount so the modal opens instantly on the first click.
    getTreatink().catch((err) => console.error("[useSdkDesigner] init failed", err));
    return () => {
      aliveRef.current = false;
      tkRef.current?.designer.close();
    };
  }, []);

  return useCallback(
    async ({ sku, onSaved, onClosed }) => {
      let tk;
      try {
        tk = await getTreatink();
      } catch (err) {
        console.error("[useSdkDesigner] SDK failed to load", err);
        toast.error("The customizer could not load. Please refresh and try again.");
        return;
      }
      // The host may have unmounted while the SDK module was still loading — opening
      // now would orphan a modal over whatever page the user navigated to.
      if (!aliveRef.current) return;
      tkRef.current = tk;
      tk.designer.open({
        sku,
        onComplete: async (result) => {
          // The modal closes on save; any failure past this point would otherwise die
          // as an unhandled rejection inside the SDK callback and the shopper would
          // believe the item was added.
          try {
            const { design, displayBlob } = await persistDesignerResult(result, cartRef.current);
            dispatch(setTkDesign(design));
            await onSaved?.(design, displayBlob);
          } catch (err) {
            console.error("[useSdkDesigner] save handling failed", err);
            toast.error("Something went wrong adding your design to the cart. Please try again.");
          }
        },
        onError: (err) => {
          console.error("[useSdkDesigner]", err?.code, err?.message);
        },
        onClose: () => onClosed?.(),
      });
    },
    [dispatch],
  );
}
